/**
 * 260907 이벤트 버블 아이콘 추가. 아이콘 세팅 방법 3가지
  1) 스프라이트 객체만 넘김
  <EventBubble leftIcon={StatusNotice} text="상담 가능" />

  2) Icon으로 크기/방향까지 지정
  <EventBubble
    leftIcon={<Icon name={StatusNotice} theme="fill" size={16} />}
    text="상담 가능"
  />

  3) 피그마 SVG 그대로
  <EventBubble
    rightIcon={
      <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden>
        <path d="..." />
      </svg>
    }
  >
    10인 이상 기업이라면
  </EventBubble>
*/

import clsx from 'clsx';
import { isValidElement, type ComponentPropsWithoutRef, type CSSProperties, type ReactElement, type ReactNode } from 'react';

import Icon, { type SvgIconInput } from '@publishing/components/ui/Icon';
import { resolveColor } from '@publishing/helpers/format';
import { resolveResponsiveValue, type ResponsiveValue } from '@publishing/helpers/responsive';

import s from './EventBubble.module.scss';

type EventBubbleIcon = ReactNode | SvgIconInput;

export type EventBubbleProps = Omit<ComponentPropsWithoutRef<'span'>, 'color'> & {
  text?: string;
  textSize?: ResponsiveValue<12 | 14 | 16 | 18 | number>;
  textColor?: string;
  bgColor?: string;
  /** 미지정 시 bgColor 로 폴백 */
  borderColor?: string;
  pos?: ResponsiveValue<'top' | 'bottom' | 'left' | 'right'>;
  offset?: ResponsiveValue<number | 'center'>;
  radius?: 'min' | 'medium' | 'max';
  leftIcon?: EventBubbleIcon;
  rightIcon?: EventBubbleIcon;
  float?: boolean; /** 위아래로 둥실거리는 공통 애니메이션(`ani_float`) */
};

type TipShape = { rounded: ReactElement; sharp: ReactElement };

/**
 * textSize 별 팁 도형 — [가로(bottom/top), 세로(left/right)].
 * 색은 CSS(`.event_bubble_arrow path`)에서 세팅 fill/stroke 속성은 svg에 넣지 않는다.
 *
 * 디자인 export 그대로지만 `d` 의 밑변(버블과 맞닿는 변)만 viewBox 밖으로 빼서 그린다.
 * 잘려나가므로 stroke 를 줘도 밑변엔 선이 생기지 않는다.
 * 방향은 bottom·left 기준이고 top·right 는 CSS 에서 180도 회전.
 */
const TIP_SHAPE: Record<number, readonly [TipShape, TipShape]> = {
  12: [
    {
      rounded: (
        <svg width="10" height="6" viewBox="0 0 10 6">
          <path d="M-0.83334 -1L4.23178 5.07813C4.63157 5.55789 5.36843 5.55789 5.76822 5.07814L10.83334 -1Z" />
        </svg>
      ),
      sharp: (
        <svg width="10" height="6" viewBox="0 0 10 6">
          <path d="M-0.91946 -1L5 5.438L10.91946 -1Z" />
        </svg>
      ),
    },
    {
      rounded: (
        <svg width="6" height="10" viewBox="0 0 6 10">
          <path d="M7 -0.83334L0.921864 4.23178C0.442109 4.63157 0.442109 5.36843 0.921863 5.76822L7 10.83334Z" />
        </svg>
      ),
      sharp: (
        <svg width="6" height="10" viewBox="0 0 6 10">
          <path d="M7 -0.91946L0.562 5L7 10.91946Z" />
        </svg>
      ),
    },
  ],
  14: [
    {
      rounded: (
        <svg width="14" height="8" viewBox="0 0 14 8">
          <path d="M0 2.43133e-06L5.49485 6.27983C6.29167 7.19048 7.70833 7.19048 8.50515 6.27983L14 0L0 2.43133e-06Z"/>
        </svg>
      ),
      sharp: (
        <svg width="14" height="8" viewBox="0 0 14 8">
          <path d="M0 2.43133e-06L7 8L14 0L0 2.43133e-06Z" />
        </svg>
      ),
    },
    {
      rounded: (
        <svg width="9" height="12" viewBox="0 0 9 12">
          <path d="M10 -0.66666L1.248071 5.16795C0.654338 5.56377 0.654338 6.43623 1.248071 6.83205L10 12.66666Z" />
        </svg>
      ),
      sharp: (
        <svg width="9" height="12" viewBox="0 0 9 12">
          <path d="M10 -0.73195L0.80273 6L10 12.73195Z" />
        </svg>
      ),
    },
  ],
  16: [
    {
      rounded: (
        <svg width="16" height="9" viewBox="0 0 16 9">
          <path d="M-0.8 -1L6.43826 8.04783C7.23891 9.04864 8.76109 9.04864 9.56174 8.04783L16.8 -1Z" />
        </svg>
      ),
      sharp: (
        <svg width="16" height="9" viewBox="0 0 16 9">
          <path d="M-0.90909 -1L8 8.8L16.90909 -1Z" />
        </svg>
      ),
    },
    {
      rounded: (
        <svg width="9" height="14" viewBox="0 0 9 14">
          <path d="M10 -0.7L1.340664 5.36154C0.20323 6.15774 0.203233 7.84226 1.340667 8.63847L10 14.7Z" />
        </svg>
      ),
      sharp: (
        <svg width="9" height="14" viewBox="0 0 9 14">
          <path d="M10 -0.82232L0.48755 7L10 14.82232Z" />
        </svg>
      ),
    },
  ],
};
TIP_SHAPE[18] = TIP_SHAPE[16];

function resolveTipShape(
  pos: 'top' | 'bottom' | 'left' | 'right',
  textSize: number,
  radius: 'min' | 'medium' | 'max',
) {
  const isVertical = pos === 'left' || pos === 'right';
  const shape = (TIP_SHAPE[textSize] ?? TIP_SHAPE[16])[isVertical ? 1 : 0];
  return radius === 'min' ? shape.sharp : shape.rounded;
}

function resolveTipOffset(offset: number | 'center') {
  return {
    pos: offset === 'center' ? '50%' : `${offset}px`,
    center: offset === 'center' ? '-50%' : '0',
  };
}

function resolveIconSize(textSize: number) {
  return textSize >= 16 ? 20 : 16;
}

function renderBubbleIcon(
  icon: EventBubbleIcon | undefined,
  size: ResponsiveValue<number>,
) {
  if (icon == null || typeof icon === 'boolean') return null;
  // SvgSprite 함수/객체 → Icon으로 감싸기
  if (typeof icon === 'function') {
    return <Icon name={icon} size={size} />;
  }
  if (typeof icon === 'object' && !isValidElement(icon) && !Array.isArray(icon)) {
    return <Icon name={icon as SvgIconInput} size={size} />;
  }
  // <svg> / <Icon /> / 기타 ReactNode
  return icon;
}

export default function EventBubble({
  leftIcon,
  rightIcon,
  text,
  textSize = 16,
  className,
  textColor = 'var(--color-gray-100)',
  bgColor = 'var(--color-gray-900)',
  borderColor,
  children,
  style,
  pos = 'bottom',
  offset = 'center',
  radius = 'max',
  float,
  ...rest
}: EventBubbleProps) {
  const [pcPos = 'bottom', moPos] = resolveResponsiveValue(pos);
  const [pcOffset = 'center', moOffset] = resolveResponsiveValue(offset);
  const [pcTextSize = 16, moTextSize] = resolveResponsiveValue(textSize);
  const pcIconSize = resolveIconSize(pcTextSize);
  const moIconSize = resolveIconSize(moTextSize ?? pcTextSize);
  const iconSize: ResponsiveValue<number> =
    moTextSize === undefined || pcIconSize === moIconSize
      ? pcIconSize
      : [pcIconSize, moIconSize];
  const hasMobilePos = moPos !== undefined || moOffset !== undefined;

  const pc = resolveTipOffset(pcOffset);
  const mo = hasMobilePos ? resolveTipOffset(moOffset ?? pcOffset) : null;

  const pcTip = resolveTipShape(pcPos, pcTextSize, radius);
  const moTip = resolveTipShape(moPos ?? pcPos, moTextSize ?? pcTextSize, radius);
  const tips =
    pcTip === moTip
      ? [{ key: 'pc', className: s.event_bubble_arrow, tip: pcTip }]
      : [
          { key: 'pc', className: clsx(s.event_bubble_arrow, s.event_bubble_arrow_pc), tip: pcTip },
          { key: 'mo', className: clsx(s.event_bubble_arrow, s.event_bubble_arrow_mo), tip: moTip },
        ];

  const bubbleStyle = {
    '--event-bubble-color': textColor ? resolveColor(textColor) : undefined,
    '--event-bubble-background': bgColor ? resolveColor(bgColor) : undefined,
    '--event-bubble-border-color': borderColor ? resolveColor(borderColor) : undefined,
    '--event-bubble-tip-pos-pc': pc.pos,
    '--event-bubble-tip-center-pc': pc.center,
    ...(mo && {
      '--event-bubble-tip-pos-mo': mo.pos,
      '--event-bubble-tip-center-mo': mo.center,
    }),
    ...style,
  } as CSSProperties;
  const bubbleClassName = clsx(
    s.event_bubble,
    s[`event_bubble_tip_${pcPos}`],
    hasMobilePos && s[`event_bubble_tip_mo_${moPos ?? pcPos}`],
    s[`event_bubble_radius_${radius}`],
    s[`event_bubble_text_${pcTextSize}`],
    moTextSize !== undefined && s[`event_bubble_text_mo_${moTextSize}`],
    float && 'ani_float',
    className,
  );

  return (
    <span {...rest} className={bubbleClassName} style={bubbleStyle}>
      {leftIcon
        ? <span className={s.event_bubble_icon} aria-hidden="true">{renderBubbleIcon(leftIcon, iconSize)}</span>
        : null}
      <span>{text ? text : children}</span>
      {rightIcon
        ? <span className={s.event_bubble_icon} aria-hidden="true">{renderBubbleIcon(rightIcon, iconSize)}</span>
        : null}
      {tips.map(({ key, className: tipClassName, tip }) => (
        <span key={key} className={tipClassName} aria-hidden="true">
          {tip}
        </span>
      ))}
    </span>
  );
}
